import {combineLatest, merge} from 'rxjs';
import {filter, map, scan, shareReplay, startWith} from 'rxjs/operators';
import {currencies$} from '@/config';
import {wsLogout$} from '@/user/auth-ws';
import {uiMsg$} from '@/ui';
import type {
  ReduceAction,
  CurrencyEntity,
  LendingAccountRaw,
  LendingAccountsData,
  LendingAccountOriginRaw,
} from '@/types';

export interface FULendingAccountAssetData {
  type: 'lending-account-asset';
  data: LendingAccountOriginRaw[];
}

export const currenciesCache$ = currencies$.pipe(
  map((currencies) => currencies.reduce((p, cur) => p.set(cur.code, cur), new Map<number, CurrencyEntity>())),
  shareReplay(1)
);

type Cache = Map<string, LendingAccountRaw>;
type Action = ReduceAction<Cache>;

const reset$ = wsLogout$.pipe(
  map(
    (): Action => (cache: Cache) => {
      cache.clear();
      return cache;
    }
  )
);
const accountsData$ = uiMsg$.pipe(
  filter((o) => o.type === 'lending-account-asset'),
  map(
    (o: FULendingAccountAssetData): LendingAccountsData =>
      o.data.map((account) => ({
        ...account,
        currencyCode: account.currency,
        currency: '',
      }))
  ),
  startWith([])
);

const action$ = combineLatest([accountsData$, currenciesCache$]).pipe(
  map(([accounts, currencies]: [LendingAccountRaw[], Map<number, CurrencyEntity>]): Action => {
    return (cache: Cache) => {
      accounts
        .filter((o) => currencies.has(o.currencyCode))
        .forEach((account) => {
          const currencyInfo = currencies.get(account.currencyCode);
          cache.set(currencyInfo.symbol, {
            ...account,
            currency: currencyInfo.symbol,
          });
        });
      return cache;
    };
  })
);

export const lendingAccountsCache$ = merge<Action[]>(action$, reset$).pipe(
  scan((p: Cache, c: Action) => c(p), new Map<string, LendingAccountRaw>())
);
